import { useMemo } from "react";
import {
  useGetTypeCategoryAndTags,
  convertTagDocsDataToOptions,
} from "../../utils";

// Maps each level to its parent field path for filtering
const PARENT_FIELD_MAP = {
  subThemes: "tags.themes.data",
  tags: "tags.subThemes.data",
};

/**
 * fetches one hierarchy level with optional parent/orphan filtering.
 * `activeAttributeThematicTabFindQuery` is AND-ed onto whichever base filter applies (parent / orphan / none),
 * and is also kept while searching so scoping survives free-text search.
 */
const useHierarchyLevelQuery = (level, { enabled, parentId, fetchOrphans, searchTerm, activeAttributeThematicTabFindQuery }) => {
  const parentField = PARENT_FIELD_MAP[level];

  const filters = useMemo(() => {
    if (!enabled) return undefined;

    // When searching, bypass hierarchy filters — search flat like the old UI.
    // Still apply activeAttributeThematicTabFindQuery (e.g. taxonomyRole scope) so search results stay scoped.
    if (searchTerm) return activeAttributeThematicTabFindQuery || undefined;

    let base;
    if (parentId && parentField) {
      base = { [`${parentField}._id`]: { $in: [parentId] } };
    } else if (fetchOrphans && parentField) {
      base = {
        $or: [
          { [parentField]: { $exists: false } },
          { [parentField]: { $size: 0 } },
          { [parentField]: null },
        ],
      };
    }

    if (activeAttributeThematicTabFindQuery) {
      return base ? { ...base, ...activeAttributeThematicTabFindQuery } : activeAttributeThematicTabFindQuery;
    }
    return base;
  }, [enabled, parentId, fetchOrphans, parentField, searchTerm, activeAttributeThematicTabFindQuery]);

  const query = useGetTypeCategoryAndTags({
    tagTypeProp: enabled ? level : null,
    filtersProp: filters,
    enableDataFetch: enabled,
    searchTerm: enabled ? searchTerm : undefined,
    lazyLoad: true,
  });

  const options = useMemo(
    () =>
      query.status === "success" && query.data
        ? convertTagDocsDataToOptions(query.data, "tags")
        : [],
    [query.status, query.data]
  );

  return {
    options,
    isLoading: query.status === "loading",
    isFetchingNextPage: query.isFetchingNextPage,
    observerRef: query.observerRef,
  };
};

/**
 * On-demand fetching for annotation hierarchy (themes, subThemes, tags).
 * Each level fetches only when its lightbox is opened.
 * Supports lazy loading (pagination) and backend filtering for parent/orphan items.
 */
export const useAnnoHierarchyOptions = ({
  hierarchyConfig = { themes: false, subThemes: false, tags: false },
  selectedThemeId = null,
  selectedSubThemeId = null,
  enableThemesFetch = false,
  enableSubThemesFetch = false,
  enableTagsFetch = false,
  searchTerms = { themes: "", subThemes: "", tags: "" },
  fetchIndependentSubThemes = false,
  fetchIndependentTags = false,
  activeAttributeThematicTabFindQuery = null,
}) => {
  const { themes: hasThemes, subThemes: hasSubThemes, tags: hasTags } = hierarchyConfig;

  const themes = useHierarchyLevelQuery("themes", {
    enabled: hasThemes && enableThemesFetch,
    searchTerm: searchTerms.themes,
  });

  const subThemes = useHierarchyLevelQuery("subThemes", {
    enabled: hasSubThemes && enableSubThemesFetch,
    parentId: selectedThemeId,
    fetchOrphans: fetchIndependentSubThemes,
    searchTerm: searchTerms.subThemes,
    activeAttributeThematicTabFindQuery,
  });

  const tags = useHierarchyLevelQuery("tags", {
    enabled: hasTags && enableTagsFetch,
    parentId: selectedSubThemeId,
    fetchOrphans: fetchIndependentTags,
    searchTerm: searchTerms.tags,
  });

  // Prefetch orphan existence checks — always run when both a level
  // and its parent level exist, so the UI can decide whether to show
  // the independent root-level option before the user drills in.
  const independentSubThemesProbe = useHierarchyLevelQuery("subThemes", {
    enabled: hasSubThemes && hasThemes,
    fetchOrphans: true,
  });

  const independentTagsProbe = useHierarchyLevelQuery("tags", {
    enabled: hasTags && (hasSubThemes || hasThemes),
    fetchOrphans: true,
  });

  return {
    optionsData: {
      themes: themes.options,
      subThemes: subThemes.options,
      tags: tags.options,
    },
    isLoading: {
      themes: themes.isLoading,
      subThemes: subThemes.isLoading,
      tags: tags.isLoading,
    },
    isFetchingNextPage: {
      themes: themes.isFetchingNextPage,
      subThemes: subThemes.isFetchingNextPage,
      tags: tags.isFetchingNextPage,
    },
    hierarchyConfig: {
      hasThemes,
      hasSubThemes,
      hasTags,
      hasIndependentSubThemes: independentSubThemesProbe.options.length > 0,
      hasIndependentTags: independentTagsProbe.options.length > 0,
    },
    observerRef: {
      themes: themes.observerRef,
      subThemes: subThemes.observerRef,
      tags: tags.observerRef,
    },
  };
};
